import { GraphQLError } from "graphql"
import { v4 as uuid } from "uuid"

import {
  noAlternativeShippingAddress,
  validAlternativeShippingAddress,
} from "@/graphql/schemas/order/utils/orderHelpers"
import { AppSettings } from "@/settings"

import { checkPermissions } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { getCustomer } from "../../shared/utils/getCustomer"
import { getEmployeeReference } from "../../shared/utils/getEmployeeReference"
import { getEmployeesByNumber } from "../../shared/utils/getEmployees"

export const mutationCreateOrderReturn = {
  async createOrderReturn(_parent, args, context, _info) {
    let { lang = AppSettings.defaultLocale(), cdn = false, values, returnItems, customerNumber } = args
    const { user } = context
    const isCashReturn = values?.documentType === "cashReturn"

    const config = AppSettings.sanityConfigByLang(lang)
    const client = context.sanityClient
    client.config({ ...config, useCdn: cdn })

    if (values?.branchId === "590") {
      throw new GraphQLError("Branch ID 590 is not allowed for checkout actions!", {
        extensions: {
          code: "NOT_ALLOWED",
        },
      })
    }

    await checkPermissions({
      user,
      domainObject: DomainObjects.ORDERS,
      operation: Operations.CREATE,
    })

    const customer = await getCustomer({ number: customerNumber })
    if (!customer) {
      throw new Error("Customer, that the cart is assigned to not found")
    }

    if (!returnItems || returnItems.length === 0) {
      throw new Error("No items to return")
    }

    const admExists = await getEmployeesByNumber(values?.admNumber, client)

    if (!admExists?.number) {
      throw new GraphQLError("ADM_NUMBER_NOT_FOUND", {
        extensions: {
          code: "NOT_FOUND",
        },
      })
    }

    // this is our default shipping address
    let customerAddress = null

    // cash returns use the shipping address from the order
    if (values?.addressId && !isCashReturn) {
      const isShippingAddress = customer?.shippingAddressIds?.includes(values.addressId)

      const foundAddress = isShippingAddress
        ? customer?.addresses?.find((address) => address?.id === values.addressId)
        : null
      customerAddress = foundAddress || customerAddress
    }

    if (customer.custom.fields.customerStatus === "E" && !isCashReturn) {
      if (noAlternativeShippingAddress(values?.deliveryObjectValues)) {
        if (values?.addressId) {
          throw new Error("Shipping address and billing address of one time customers need to be different")
        }
      } else if (!validAlternativeShippingAddress(values?.deliveryObjectValues)) {
        throw new Error("Alternative shipping address is incomplete")
      }
    }

    let contactPersonIds = customer?.addresses
      ?.map((address) => {
        if (
          values?.contactPersons?.includes(address?.id) &&
          !customer?.billingAddressIds?.includes(address?.id) &&
          !customer?.shippingAddressIds?.includes(address?.id)
        ) {
          return address?.email
        }
        return null
      })
      .filter((n) => n)

    const createdBy = await getEmployeeReference({
      employeeNumber: user.employeeNumber,
      sanityClient: client,
    })

    const billingAddress = getCustomerBillingAddress(customer)
    delete customerAddress?.custom
    delete customerAddress?.externalId

    // Use deliveryObjectValues for shipping address when:
    // 1. Cash returns (always use the order's shipping address)
    // 2. Regular returns with no addressId (document shipping address case)
    const hasDeliveryObjectData =
      values?.deliveryObjectValues?.name || values?.deliveryObjectValues?.street || values?.deliveryObjectValues?.city

    if ((isCashReturn || (!values?.addressId && hasDeliveryObjectData)) && values?.deliveryObjectValues) {
      const { name, companyName, street, postalCode, city, country, additionalLocation } =
        values.deliveryObjectValues || {}

      customerAddress = {
        firstName: name || "",
        lastName: companyName || "",
        streetName: street || "",
        postalCode: postalCode || "",
        city: city || "",
        country: country || "",
        additionalAddressInfo: additionalLocation || "",
      }
    }

    const lineItems = returnItems.map((item) => {
      const orderNumber = item?.number
      const orderId = item?.orderNumber

      delete item?.number
      delete item?.orderNumber

      return { _key: uuid(), orderId, orderNumber, ...item }
    })

    const payload = {
      _type: isCashReturn ? "orderCashReturn" : "orderReturn",
      shippingMethodName: values?.shippingMethodName || "",
      customerNumber: customer?.customerNumber || "",
      billingAddress: billingAddress || {},
      shippingAddress: customerAddress || {},
      lineItems: lineItems || [],
      createdAt: new Date().toISOString(),
      modificationSource: "Sanity",
      ...(createdBy && { createdBy }),
      deliveryObject: !isCashReturn && values?.deliveryObjectValues ? values?.deliveryObjectValues : {},
      contactPersons: contactPersonIds?.length > 0 ? contactPersonIds.join(", ") : "",
      documentType: values?.documentType || "",
      admNumber: values?.admNumber || "",
      customerExternalId: customer?.externalId || "",
      branchId: values?.branchId || "",
      collectiveInvoiceType: values?.collectiveInvoiceType || "",
    }

    // The latest orders are missing in the test ERP, so only test with orders that are older than 4 weeks.
    // https://reply.atlassian.net/browse/EINZA-1833?focusedCommentId=69620
    try {
      const insertedOrderReturn = await client.create(payload)

      return {
        ...insertedOrderReturn,
        id: insertedOrderReturn._id,
        rev: insertedOrderReturn._rev,
      }
    } catch (error) {
      throw new Error(error.message)
    }
  },
}

const getCustomerBillingAddress = (customer) => {
  const billingAddress = customer?.addresses?.find((address) => address?.id === customer?.defaultBillingAddressId)

  if (!billingAddress) {
    return null
  }

  delete billingAddress?.custom
  delete billingAddress?.externalId

  return billingAddress
}
